import React, { useEffect, useState } from 'react'
import { Spinner, Tab } from 'react-bootstrap'
import { useGetAllScorecardsQuery, useGetLatestScorecardResultsForUserQuery } from '../../store/ai/scorecard'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useNavigate, useParams } from 'react-router-dom'
import './ClientProfile.scss'
import { Tabs } from '../../components/Tabs/Tabs'
import { ClientProfileForm } from './ClientProfileForm'
import { Scorecard as ScorecardModel, ScorecardType } from '../../models/Scorecard'
import { sortClientProfiles } from '../../common/clientProfile'
import { Button } from '../../components/Button/Button'
import { User } from '../../models/User'
import { useExecuteClientProfileInitialCRMSyncMutation } from '../../store/ai/aiExecute'
import { useTracking } from '../../apps/professional-app/fa/common/tracking'

interface ClientProfileProps {
  contact: User
}

export const ClientProfile: React.FC<ClientProfileProps> = ({ contact }) => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const {
    data: latestScorecardResults,
    isFetching: getLatestScorecardResultsIsFetching,
    refetch: refetchLatestScorecardResults,
  } = useGetLatestScorecardResultsForUserQuery(
    {
      accountId,
      userId: contact.id,
    },
    { refetchOnMountOrArgChange: true },
  )
  const { data: scorecards, isFetching: getScorecardsFetching } = useGetAllScorecardsQuery(
    {
      accountId,
      type: ScorecardType.CLIENT_PROFILE,
    },
    { refetchOnMountOrArgChange: true },
  )
  const [executeClientProfileCRMSync, { isLoading: initialSyncIsLoading }] = useExecuteClientProfileInitialCRMSyncMutation()

  const { scorecardTab = '' } = useParams()
  const [selectedTab, setSelectedTab] = useState(scorecardTab)
  const [sortedScorecards, setSortedScorecards] = useState<ScorecardModel[]>([])
  const navigate = useNavigate()
  const [showInitialCRMSync, setShowInitialCRMSync] = useState(false)

  useEffect(() => {
    if (getLatestScorecardResultsIsFetching) {
      setShowInitialCRMSync(false)
      return
    } else if (
      latestScorecardResults?.length === 0 &&
      contact.integrations?.some((i) => i.type === 'crm') &&
      contact.roles.some((role) => role.name === 'Client')
    ) {
      setShowInitialCRMSync(true)
    }
  }, [getLatestScorecardResultsIsFetching, latestScorecardResults, contact])

  useEffect(() => {
    if (scorecards) {
      setSortedScorecards(sortClientProfiles([...scorecards.data]))
    }
  }, [scorecards])

  useEffect(() => {
    if (sortedScorecards.length > 0 && !selectedTab) {
      changeTab(sortedScorecards[0].id)
    }
  }, [sortedScorecards, selectedTab])

  const TabTitle = ({ title }: { title: string }) => <div className='d-flex align-items-center'>{title}</div>

  const changeTab = (key: string | null) => {
    if (key) {
      setSelectedTab(key)
      navigate(`/library/manage-contacts/contacts/${contact.id}/tabs/scorecards/${key}`, { replace: true })
    }
  }

  const _onInitialClientProfileCRMSync = async () => {
    await executeClientProfileCRMSync({
      accountId,
      payload: {
        userId: contact.id,
      },
    }).unwrap()
    refetchLatestScorecardResults()
  }

  const onInitialClientProfileCRMSync = tracked(_onInitialClientProfileCRMSync, {
    eventName: 'CLIENT_PROFILE_INITIAL_CRM_SYNC_EXECUTED',
    eventDetails: { userId: contact.id },
  })

  const renderScorecardList = () => {
    if (getScorecardsFetching) {
      return <Spinner />
    }
    if (!sortedScorecards || sortedScorecards.length === 0) {
      return 'No client profile available.'
    }

    const getResultForScorecard = (scorecardId: string) => {
      if (!latestScorecardResults) {
        return null
      }

      return latestScorecardResults.find((result) => result.scorecardId === scorecardId) || null
    }

    return (
      <>
        {showInitialCRMSync && (
          <div className='d-flex zocks-bg-color-primary-light zocks-gap-2.5 p-3 justify-content-between rounded-1 mb-3'>
            <div className='d-flex align-self-center gap-3 align-items-center'>
              <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
              <span className='zocks-text-gray-900'>You can run an initial sync to populate the client profile using CRM information.</span>
            </div>
            <div>
              <Button
                className='d-flex p-2 align-items-center text-nowrap'
                variant='primary'
                onClick={() => {
                  onInitialClientProfileCRMSync()
                }}
                loading={initialSyncIsLoading}
              >
                Initial sync from CRM
              </Button>
            </div>
          </div>
        )}
        <div className='d-flex gap-5 scorecards-tab-wrapper'>
          <Tabs onSelect={changeTab} mountOnEnter activeKey={selectedTab} id='scorecards-tabs' className='vertical-tab'>
            {sortedScorecards.map((scorecard) => {
              return (
                <Tab key={scorecard.id} mountOnEnter className='mt-2' eventKey={scorecard.id} title={<TabTitle title={scorecard.name} />}>
                  <ClientProfileForm scorecard={scorecard} contactId={contact.id} scorecardResult={getResultForScorecard(scorecard.id)} />
                </Tab>
              )
            })}
          </Tabs>
        </div>
      </>
    )
  }

  return <>{renderScorecardList()}</>
}
